import {
  acceptKioskConsentResponseSchema,
  createKioskTokenResponseSchema,
  kioskCheckinResponseSchema,
  kioskCompleteResponseSchema,
  kioskUpdateCheckinResponseSchema,
  type AcceptKioskConsentResponse,
  type CreateKioskTokenResponse,
  type KioskCheckinResponse,
  type KioskCompleteResponse,
  type KioskUpdateCheckinRequest,
  type KioskUpdateCheckinResponse,
  type LegalDocumentType,
} from '@emr/shared';
import { z } from 'zod';

import { apiRequest } from './client.ts';

/**
 * The kiosk check-in client — two audiences, matching `server/src/kiosk/routes.ts`'s own split:
 *
 *   - `createKioskToken`/`revokeKioskToken` are **staff** calls, made from inside the ordinary
 *     authenticated app (`KioskTokenPanel.tsx`), so they go through `apiRequest` exactly like every
 *     other staff API call — the session cookie carries the auth, no `Authorization` header.
 *   - `getKioskCheckin`/`updateKioskCheckin`/`acceptKioskConsent`/`completeKioskCheckin` are the
 *     **patient's own** calls, made from `routes/kiosk/` with no staff session at all — every one of
 *     these instead carries the bearer token in an `Authorization` header, via `apiRequest`'s
 *     `headers` option (added specifically for this file — see that option's own comment).
 */

const revokeKioskTokenResponseSchema = z.void();

export async function createKioskToken(
  patientId: string,
  documentTypes?: readonly LegalDocumentType[],
): Promise<CreateKioskTokenResponse> {
  return apiRequest('/api/kiosk/tokens', createKioskTokenResponseSchema, {
    method: 'POST',
    body: documentTypes === undefined ? { patientId } : { patientId, documentTypes },
  });
}

export async function revokeKioskToken(tokenId: string): Promise<void> {
  await apiRequest(`/api/kiosk/tokens/${tokenId}/revoke`, revokeKioskTokenResponseSchema, {
    method: 'POST',
    body: {},
  });
}

function bearerHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

export async function getKioskCheckin(
  token: string,
  signal?: AbortSignal,
): Promise<KioskCheckinResponse> {
  return apiRequest('/api/kiosk/checkin', kioskCheckinResponseSchema, {
    headers: bearerHeaders(token),
    ...(signal === undefined ? {} : { signal }),
  });
}

export async function updateKioskCheckin(
  token: string,
  changes: KioskUpdateCheckinRequest,
): Promise<KioskUpdateCheckinResponse> {
  return apiRequest('/api/kiosk/checkin', kioskUpdateCheckinResponseSchema, {
    method: 'PATCH',
    body: changes,
    headers: bearerHeaders(token),
  });
}

export async function acceptKioskConsent(
  token: string,
  documentType: LegalDocumentType,
): Promise<AcceptKioskConsentResponse> {
  return apiRequest('/api/kiosk/checkin/consents/accept', acceptKioskConsentResponseSchema, {
    method: 'POST',
    body: { documentType },
    headers: bearerHeaders(token),
  });
}

export async function completeKioskCheckin(token: string): Promise<KioskCompleteResponse> {
  return apiRequest('/api/kiosk/checkin/complete', kioskCompleteResponseSchema, {
    method: 'POST',
    body: {},
    headers: bearerHeaders(token),
  });
}
